import { humanize, Icon } from '@garnet/ui';
import { CAPTURE_OPTIONS, type CaptureKind } from '../data/shape.js';

/**
 * One required capture rendered with the control its method-template kind demands
 * (PRD §10.3): a camera placeholder for photos, an attach affordance for lab files,
 * segmented option sets for graded fields, a structured input for text, a confirm
 * checkbox otherwise. The leading tick reflects whether the capture is present — the
 * submission gate (shape.canSubmit) reads exactly that presence.
 */
export interface CaptureFieldProps {
  capKey: string;
  kind: CaptureKind;
  value: string;
  done: boolean;
  /** Mark a tap-to-capture kind (photo/file/check) present or absent. */
  onToggle: () => void;
  /** Set the value of a valued kind (grade/seal/verdict/status/text); '' clears it. */
  onValue: (value: string) => void;
}

const SEG_KINDS = new Set<CaptureKind>(['grade', 'seal', 'verdict', 'status']);

function Tick({ done }: { done: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        flex: 'none',
        border: `1px solid ${done ? 'var(--verified)' : 'var(--rule)'}`,
        background: done ? 'var(--verified)' : 'var(--panel)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {done && <Icon name="check" size={12} />}
    </span>
  );
}

export function CaptureField({ capKey, kind, value, done, onToggle, onValue }: CaptureFieldProps) {
  const label = humanize(capKey);

  if (SEG_KINDS.has(kind)) {
    const options = CAPTURE_OPTIONS[kind as 'grade' | 'seal' | 'verdict' | 'status'];
    return (
      <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--rule)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Tick done={done} />
          <span style={{ fontSize: 13.5, color: done ? 'var(--ink)' : 'var(--muted)' }}>{label}</span>
        </div>
        <div role="group" aria-label={label} style={{ display: 'flex', gap: 6 }}>
          {options.map((o) => {
            const on = value === o;
            return (
              <button
                key={o}
                type="button"
                aria-pressed={on}
                onClick={() => onValue(o)}
                style={{
                  flex: 1,
                  fontFamily: 'var(--mono)',
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: '8px 0',
                  borderRadius: 7,
                  cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--ink)' : 'var(--rule)'}`,
                  background: on ? 'var(--ink)' : 'var(--panel)',
                  color: on ? 'var(--paper)' : 'var(--muted)',
                }}
              >
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (kind === 'text') {
    return (
      <label style={{ display: 'block', padding: '11px 12px', borderBottom: '1px solid var(--rule)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Tick done={done} />
          <span style={{ fontSize: 13.5, color: done ? 'var(--ink)' : 'var(--muted)' }}>{label}</span>
        </span>
        <input
          className="input mono"
          value={value}
          onChange={(e) => onValue(e.target.value)}
          placeholder={`enter ${label.toLowerCase()}`}
          aria-label={label}
          style={{ width: '100%', boxSizing: 'border-box' }}
        />
      </label>
    );
  }

  // tap-to-capture: photo (camera placeholder), file (attach), check (confirm)
  const verb = kind === 'photo' ? 'Capture photo' : kind === 'file' ? 'Attach file' : 'Confirm';
  const icon = kind === 'photo' ? 'eye' : kind === 'file' ? 'upload' : 'check';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={done}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        textAlign: 'left',
        padding: '11px 12px',
        borderBottom: '1px solid var(--rule)',
        border: 0,
        borderBottomColor: 'var(--rule)',
        background: 'none',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      <Tick done={done} />
      <span style={{ fontSize: 13.5, color: done ? 'var(--ink)' : 'var(--muted)' }}>{label}</span>
      <span
        style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          color: 'var(--muted)',
        }}
      >
        <Icon name={icon} size={15} />
        {done ? 'captured' : verb}
      </span>
    </button>
  );
}
